/**
 * SmsSubscribers — one snippet, both modules.
 *
 * A tiny inline UI that lets the owner of a trip/listing manage the
 * list of phone numbers that get an SMS every time someone checks in
 * (via <CheckinButton/>). Add, view, remove.
 *
 * Where it's used:
 *   • /roadtrip           — RoadtripPage owner view (trip planners)
 *   • /finder/:listing_id — Listing owner view
 *   • Anywhere with a stable (source, targetKey) pair.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, PlusCircle, Loader2, Users } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL ||
             (typeof window !== "undefined" ? window.location.origin : "");

export default function SmsSubscribers({
  source,
  targetKey,
  title = "SMS group",
  testid = "sms-subscribers",
}) {
  const [subs, setSubs] = useState([]);
  const [phone, setPhone] = useState("");
  const [name,  setName]  = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!source || !targetKey) return;
    setLoading(true);
    try {
      const r = await fetch(
        `${API}/api/checkin/subscribers?source=${encodeURIComponent(source)}`
        + `&key=${encodeURIComponent(targetKey)}`,
        { credentials: "include" },
      );
      const j = await r.json();
      setSubs(j.subscribers || []);
    } catch (e) {
      /* silent — box just shows empty state */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [source, targetKey]);

  const add = async (e) => {
    e?.preventDefault?.();
    if (!phone.trim()) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/checkin/subscribers`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ source, key: targetKey,
                               phone: phone.trim(),
                               name:  name.trim() || null }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      toast.success(`${j.phone_e164} added to SMS group`);
      setPhone(""); setName("");
      await load();
    } catch (err) {
      toast.error(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (sub_id, phone_e164) => {
    if (!window.confirm(`Remove ${phone_e164} from the SMS group?`)) return;
    try {
      await fetch(`${API}/api/checkin/subscribers/${sub_id}`, {
        method: "DELETE", credentials: "include",
      });
      await load();
      toast.success("Removed");
    } catch (err) { toast.error(err.message || String(err)); }
  };

  return (
    <div className="hp-sms-subs rounded-2xl border border-stone-200
                     dark:border-stone-700 bg-white/70 dark:bg-stone-900/60
                     p-4 space-y-3"
          data-testid={testid}>
      <div className="flex items-center gap-2">
        <Users size={18} className="text-amber-500"/>
        <h3 className="text-sm font-bold tracking-wide uppercase
                        text-stone-700 dark:text-stone-200">
          {title}
        </h3>
        {loading && <Loader2 size={14} className="animate-spin text-stone-400"/>}
      </div>

      {subs.length > 0 ? (
        <ul className="space-y-1.5">
          {subs.map((s) => (
            <li key={s.sub_id}
                 data-testid={`${testid}-row-${s.sub_id}`}
                 className="flex items-center justify-between gap-2
                            text-sm text-stone-700 dark:text-stone-200">
              <span className="tabular-nums">
                {s.phone_e164}
                {s.name && <span className="ml-2 text-stone-500">({s.name})</span>}
              </span>
              <button
                type="button"
                onClick={() => remove(s.sub_id, s.phone_e164)}
                data-testid={`${testid}-remove-${s.sub_id}`}
                className="text-stone-400 hover:text-red-500"
                title="Remove from SMS group"
              ><Trash2 size={14}/></button>
            </li>
          ))}
        </ul>
      ) : !loading && (
        <p className="text-xs text-stone-500 italic">
          No SMS subscribers yet. Add a phone below and it&apos;ll receive a
          text every time someone checks in.
        </p>
      )}

      <form onSubmit={add}
             className="flex flex-col gap-2 pt-2 border-t border-stone-200
                        dark:border-stone-700">
        <div className="flex gap-2">
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+61412345678"
            required
            maxLength={20}
            data-testid={`${testid}-phone`}
            className="flex-1 h-9 rounded-lg border border-stone-300
                       dark:border-stone-700 bg-white dark:bg-stone-900
                       px-3 text-sm tabular-nums"
          />
          <input
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 60))}
            placeholder="Name (optional)"
            maxLength={60}
            data-testid={`${testid}-name`}
            className="flex-1 h-9 rounded-lg border border-stone-300
                       dark:border-stone-700 bg-white dark:bg-stone-900
                       px-3 text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={busy || !phone.trim()}
          data-testid={`${testid}-add`}
          className="inline-flex items-center justify-center gap-2 h-9
                     rounded-lg font-semibold text-sm text-stone-900
                     bg-amber-400 hover:bg-amber-300 disabled:opacity-50
                     transition-colors"
        >
          {busy
            ? <><Loader2 size={14} className="animate-spin"/> Adding…</>
            : <><PlusCircle size={14}/> Add to SMS group</>}
        </button>
      </form>
    </div>
  );
}
